/* global window */
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@fluentui/react-components";
import CommonTooltip from "../../common/CommonTooltip";
import { insert } from "../../../state/structureSlice";
import { add, onChange as onBorderChange } from "../../../state/bordersSlice";
import { onChange as onSpacerChange } from "../../../state/spacerStylesSlice";
import {
  InsertBorderFilled,
  InsertBorderFilledHorizontal,
  InsertColumnFilled,
  InsertRowFilled,
  InsertSpacerFilled,
  InsertSpacerFilledHorizontal,
} from "../../../assets/icons";
import { v4 as uuidv4 } from "uuid";
import { batchGroupBy } from "../../../helpers/batchGroupBy";
import { select, reset } from "../../../state/selectionSlice";
import { inheritAboveRowStyle, inheritLeftColumnStyle } from "../../../helpers/stylesInBatch";
import TrackedButton from "../../common/TrackedButton";
import { getPinsForMoving } from "../../../helpers/grid";

const useClasses = makeStyles({
  Header: {
    fontFamily: "Arial",
    fontSize: 10,
    fontWeight: 700,
    lineGeight: 11.5,
    textAlign: "left",
  },
  PanelButton: {
    justifyContent: "left",
    fontFamily: "Arial",
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: "0.02em",
  },
  IconButton: {
    justifyContent: "left",
    fontFamily: "Arial",
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: "0.02em",
    minWidth: 0,
    width: "fit-content",
  },
  AccordionHeader: {
    "& > button": {
      minHeight: 0,
    },
  },
});

const StructureMenuItem = () => {
  const classes = useClasses();
  const { t } = useTranslation();
  const selection = useSelector((state) => state.selection);
  const cells = useSelector((state) => state.gridd.present.structure.cells);
  const currentStyleSheet = useSelector(
    (state) => state.styleSheet.sheets[state.styleSheet.selectedSheet] ?? state.styleSheet.sheets["default"]
  );
  const dispatch = useDispatch();
  const [selectedSizes, setSelectedSizes] = React.useState([]);
  const structure = useSelector((state) => state.gridd.present.structure);

  function display(v) {
    return v[0] === "s" ? `${parseInt(v.replace("s", ""))} px` : v === "*" ? "1x" : v?.toFixed(4) + "x";
  }

  React.useEffect(() => {
    const colPins = getPinsForMoving(structure.columns, window.innerWidth);
    const rowPins = getPinsForMoving(structure.rows, window.innerHeight);

    setSelectedSizes(
      structure.columns
        .map((c, ci) =>
          selection.columns?.includes(ci)
            ? { id: ci, value: display(c), real: colPins[ci + 1] - colPins[ci] }
            : undefined
        )
        .concat(
          structure.rows.map((r, ri) =>
            selection.rows?.includes(ri)
              ? { id: ri, value: display(r), real: rowPins[ri + 1] - rowPins[ri] }
              : undefined
          )
        )
        .filter((f) => f !== undefined && f !== null)
    );
  }, [selection, structure]);

  return (
    <div
      id="context_menu_item_structure"
      style={{
        pointerEvents: "all",
        borderRadius: "4px",
        background: "white",
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "auto auto auto", width: 240 }}>
        <TrackedButton
          id="insert_col_or_row_button"
          appearance="subtle"
          className={classes.IconButton}
          onClick={() => {
            if (selection.slot) {
              const newCellIds = selection.slot.vertical ? cells.map(() => uuidv4()) : cells[0].map(() => uuidv4());

              batchGroupBy.start();
              dispatch(
                insert({
                  newCellIds,
                  columnIndex: selection.slot.vertical ? selection.slot.index : undefined,
                  rowIndex: !selection.slot.vertical ? selection.slot.index : undefined,
                })
              );

              if (selection.slot.index > 0) {
                if (selection.slot.vertical) {
                  inheritLeftColumnStyle(dispatch, selection.slot.index, cells, newCellIds);
                } else {
                  inheritAboveRowStyle(dispatch, selection.slot.index, cells, newCellIds);
                }
              }

              dispatch(
                onBorderChange({
                  columnIndexAdded: selection.slot.vertical ? selection.slot.index : undefined,
                  rowIndexAdded: !selection.slot.vertical ? selection.slot.index : undefined,
                })
              );
              dispatch(
                onSpacerChange({
                  columnIndexAdded: selection.slot.vertical ? selection.slot.index - 1 : undefined,
                  rowIndexAdded: !selection.slot.vertical ? selection.slot.index - 1 : undefined,
                })
              );
              batchGroupBy.end();

              // Reset selection to close the menu
              dispatch(reset());
            } else if (selection.borders.length > 0) {
              const border = selection.borders[0];
              const newCellIds = border.isVertical ? cells.map(() => uuidv4()) : cells[0].map(() => uuidv4());
              batchGroupBy.start();
              dispatch(
                insert({
                  newCellIds,
                  columnIndex: border.isVertical ? border.index : undefined,
                  rowIndex: !border.isVertical ? border.index : undefined,
                })
              );

              if (border.index > 0) {
                if (border.isVertical) {
                  inheritLeftColumnStyle(dispatch, border.index, cells, newCellIds);
                } else {
                  inheritAboveRowStyle(dispatch, border.index, cells, newCellIds);
                }
              }

              dispatch(
                onBorderChange({
                  columnIndexAdded: border.isVertical ? border.index : undefined,
                  rowIndexAdded: !border.isVertical ? border.index : undefined,
                })
              );
              dispatch(
                onSpacerChange({
                  columnIndexAdded: border.isVertical ? border.index : undefined,
                  rowIndexAdded: !border.isVertical ? border.index : undefined,
                })
              );
              batchGroupBy.end();

              // Reset selection to close the menu
              dispatch(reset());
            } else {
              const newCellIds =
                selection.columns.length > 0
                  ? cells.map(() => uuidv4())
                  : selection.rows.length > 0
                    ? cells[0].map(() => uuidv4())
                    : undefined;

              batchGroupBy.start();
              dispatch(
                insert({
                  newCellIds,
                  columnIndex: selection.columns.length > 0 ? selection.columns[0] : undefined,
                  rowIndex: selection.rows.length > 0 ? selection.rows[0] : undefined,
                })
              );

              if (selection.columns.length > 0 && selection.columns[0] > 0) {
                inheritLeftColumnStyle(dispatch, selection.columns[0], cells, newCellIds);
              } else if (selection.rows.length > 0 && selection.rows[0] > 0) {
                inheritAboveRowStyle(dispatch, selection.rows[0], cells, newCellIds);
              }

              dispatch(
                onBorderChange({
                  columnIndexAdded: selection.columns.length > 0 ? selection.columns[0] : undefined,
                  rowIndexAdded: selection.rows.length > 0 ? selection.rows[0] : undefined,
                })
              );
              dispatch(
                onSpacerChange({
                  columnIndexAdded: selection.columns.length > 0 ? selection.columns[0] : undefined,
                  rowIndexAdded: selection.rows.length > 0 ? selection.rows[0] : undefined,
                })
              );
              batchGroupBy.end();

              // Reset selection to close the menu
              dispatch(reset());
            }
          }}
        >
          <div style={{ display: "grid", gridTemplateRows: "auto auto" }}>
            {selection.slot?.vertical || selection.columns?.length > 0 || selection.borders[0]?.isVertical ? (
              <InsertColumnFilled style={{ justifySelf: "center", height: 24 }} />
            ) : (
              <InsertRowFilled style={{ justifySelf: "center", height: 24 }} />
            )}
            {selection.slot?.vertical || selection.columns?.length > 0 || selection.borders[0]?.isVertical
              ? t("menu.structure.insert.column")
              : t("menu.structure.insert.row")}
          </div>
        </TrackedButton>
        <TrackedButton
          id="insert_border_button"
          appearance="subtle"
          className={classes.IconButton}
          onClick={() => {
            if (selection.slot) {
              const id = uuidv4();
              const defaultBorderName = currentStyleSheet.defaults?.border;
              const defaultBorder = currentStyleSheet.borders?.find((b) => b.name === defaultBorderName);
              const borderColor =
                currentStyleSheet.colors?.find((c) => c.name === defaultBorder?.color)?.value ?? "black";
              dispatch(
                add({
                  id,
                  columnIndex: selection.slot.vertical ? selection.slot.index : undefined,
                  rowIndex: !selection.slot.vertical ? selection.slot.index : undefined,
                  preset: defaultBorder?.name,
                  weight: defaultBorder?.width,
                  color: borderColor,
                  style: defaultBorder?.type,
                })
              );
              dispatch(reset());
            } else {
              const id = uuidv4();
              if (selection.borders.length > 0) {
                const border = selection.borders[0];
                const defaultBorderName = currentStyleSheet.defaults?.border;
                const defaultBorder = currentStyleSheet.borders?.find((b) => b.name === defaultBorderName);
                const borderColor =
                  currentStyleSheet.colors?.find((c) => c.name === defaultBorder?.color)?.value ?? "black";
                batchGroupBy.start();
                dispatch(
                  add({
                    id,
                    columnIndex: border.isVertical ? border.index : undefined,
                    rowIndex: !border.isVertical ? border.index : undefined,
                    preset: defaultBorder?.name,
                    weight: defaultBorder?.width,
                    color: borderColor,
                    style: defaultBorder?.type,
                  })
                );
                batchGroupBy.end();
                dispatch(reset());
              } else {
                const defaultBorderName = currentStyleSheet.defaults?.border;
                const defaultBorder = currentStyleSheet.borders?.find((b) => b.name === defaultBorderName);
                const borderColor =
                  currentStyleSheet.colors?.find((c) => c.name === defaultBorder?.color)?.value ?? "black";
                dispatch(
                  add({
                    id,
                    columnIndex: selection.columns.length > 0 ? selection.columns[0] : undefined,
                    rowIndex: selection.rows.length > 0 ? selection.rows[0] : undefined,
                    preset: defaultBorder?.name,
                    weight: defaultBorder?.width,
                    color: borderColor,
                    style: defaultBorder?.type,
                  })
                );
                dispatch(reset());
              }
              dispatch(
                select({
                  border: {
                    id,
                    index: selection.columns.length > 0 ? selection.columns[0] : selection.rows[0],
                    isVertical: selection.columns.length > 0,
                  },
                })
              );
            }
          }}
        >
          <div style={{ display: "grid", gridTemplateRows: "auto auto" }}>
            {selection.slot?.vertical || selection.columns?.length > 0 || selection.borders[0]?.isVertical ? (
              <InsertBorderFilled style={{ justifySelf: "center", height: 24 }} />
            ) : (
              <InsertBorderFilledHorizontal style={{ justifySelf: "center", height: 24 }} />
            )}
            {t("menu.structure.insert.border")}
          </div>
        </TrackedButton>
        <TrackedButton
          id="insert_spacer_button"
          appearance="subtle"
          className={classes.IconButton}
          onClick={() => {
            if (selection.slot) {
              batchGroupBy.start();
              dispatch(
                insert({
                  columnIndex: selection.slot.vertical ? selection.slot.index : undefined,
                  rowIndex: !selection.slot.vertical ? selection.slot.index : undefined,
                  spacer: true,
                })
              );
              dispatch(
                onBorderChange({
                  columnIndexAdded: selection.slot.vertical ? selection.slot.index - 1 : undefined,
                  rowIndexAdded: !selection.slot.vertical ? selection.slot.index - 1 : undefined,
                })
              );
              dispatch(
                onSpacerChange({
                  columnIndexAdded: selection.slot.vertical ? selection.slot.index - 1 : undefined,
                  rowIndexAdded: !selection.slot.vertical ? selection.slot.index - 1 : undefined,
                })
              );
              batchGroupBy.end();
              // Reset selection to close the menu
              dispatch(reset());
            } else if (selection.borders.length > 0) {
              const border = selection.borders[0];
              batchGroupBy.start();
              dispatch(
                insert({
                  columnIndex: border.isVertical ? border.index : undefined,
                  rowIndex: !border.isVertical ? border.index : undefined,
                  spacer: true,
                })
              );
              dispatch(
                onBorderChange({
                  columnIndex: border.isVertical ? border.index : undefined,
                  rowIndex: !border.isVertical ? border.index : undefined,
                })
              );
              dispatch(
                onSpacerChange({
                  columnIndex: border.isVertical ? border.index : undefined,
                  rowIndex: !border.isVertical ? border.index : undefined,
                })
              );
              batchGroupBy.end();
              // Reset selection to close the menu
              dispatch(reset());
            } else {
              batchGroupBy.start();
              dispatch(
                insert({
                  columnIndex: selection.columns.length > 0 ? selection.columns[0] : undefined,
                  rowIndex: selection.rows.length > 0 ? selection.rows[0] : undefined,
                  spacer: true,
                })
              );
              dispatch(
                onBorderChange({
                  columnIndexAdded: selection.columns.length > 0 ? selection.columns[0] : undefined,
                  rowIndexAdded: selection.rows.length > 0 ? selection.rows[0] : undefined,
                })
              );
              dispatch(
                onSpacerChange({
                  columnIndexAdded: selection.columns.length > 0 ? selection.columns[0] : undefined,
                  rowIndexAdded: selection.rows.length > 0 ? selection.rows[0] : undefined,
                })
              );
              batchGroupBy.end();
              // Reset selection to close the menu
              dispatch(reset());
            }
          }}
        >
          <div style={{ display: "grid", gridTemplateRows: "auto auto" }}>
            {selection.slot?.vertical || selection.columns?.length > 0 || selection.borders[0]?.isVertical ? (
              <InsertSpacerFilled style={{ justifySelf: "center", height: 24 }} />
            ) : (
              <InsertSpacerFilledHorizontal style={{ justifySelf: "center", height: 24 }} />
            )}
            {t("menu.structure.insert.spacer")}
          </div>
        </TrackedButton>
      </div>

      {selection.columns?.length > 0 || selection.rows?.length > 0 ? (
        <div>
          <div>{selection.columns?.length > 0 ? t("selection.columns.sizes") : t("selection.rows.sizes")}</div>
          {selectedSizes.map((s) => (
            <CommonTooltip key={s.id} content={t("selection.real.tooltip", { ...s, realFixed: s.real.toFixed(2) })}>
              <div>{s.value}</div>
            </CommonTooltip>
          ))}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default StructureMenuItem;
